/**
 * Service Worker client — registration + the prefetch channel the carousel uses to warm
 * the cache for game N+2 while N plays and N+1 sits compiled and paused.
 * The worker itself is public/sw.js (plain JS so it needs no build step).
 */

export interface CacheStatus {
  games: string[];
  shell: number;
  version: string;
}

let registration: ServiceWorkerRegistration | null = null;

export function serviceWorkerSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!serviceWorkerSupported()) return null;
  // In dev, only register when explicitly asked (?sw=1) so HMR is never served from cache.
  if (import.meta.env.DEV && !new URLSearchParams(location.search).has('sw')) return null;
  try {
    registration = await navigator.serviceWorker.register('/sw.js', { scope: '/', type: 'classic' });
    return registration;
  } catch (err) {
    console.warn('[blitz] service worker registration failed', err);
    return null;
  }
}

export function serviceWorkerActive(): boolean {
  return !!navigator.serviceWorker?.controller;
}

async function controller(): Promise<ServiceWorker | null> {
  if (!serviceWorkerSupported()) return null;
  if (navigator.serviceWorker.controller) return navigator.serviceWorker.controller;
  // Freshly installed: wait (briefly) until the SW controls this page.
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(navigator.serviceWorker.controller), 1500);
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        clearTimeout(t);
        resolve(navigator.serviceWorker.controller);
      },
      { once: true },
    );
  });
}

function request<T>(msg: unknown, timeoutMs = 4000): Promise<T | null> {
  return controller().then((sw) => {
    if (!sw) return null;
    return new Promise<T | null>((resolve) => {
      const channel = new MessageChannel();
      const t = setTimeout(() => resolve(null), timeoutMs);
      channel.port1.onmessage = (ev) => {
        clearTimeout(t);
        resolve(ev.data as T);
      };
      sw.postMessage(msg, [channel.port2]);
    });
  });
}

/** Ask the SW to fetch + cache the given URLs (game N+2's .wasm/.data/thumbnail). */
export function prefetch(urls: string[]): Promise<{ ok: string[]; failed: string[] } | null> {
  const cacheable = urls.filter((u) => !u.startsWith('blob:'));
  if (!cacheable.length) return Promise.resolve({ ok: [], failed: [] });
  return request<{ ok: string[]; failed: string[] }>({ type: 'PREFETCH', urls: cacheable });
}

export function cacheStatus(): Promise<CacheStatus | null> {
  return request<CacheStatus>({ type: 'CACHE_STATUS' });
}

export function clearGameCache(): Promise<{ cleared: boolean } | null> {
  return request<{ cleared: boolean }>({ type: 'CLEAR_GAMES' });
}
